import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { History } from 'lucide-react'
import type { ActivityEvent } from '@ousse/shared'
import { api } from '../lib/api'
import { activityLink, formatActivityMessage, formatRelativeTime } from '../lib/activity'
import { useHousehold } from '../lib/household'
import { ErrorAlert } from '../components/ui'

/** Fil d'activité du foyer : qui a fait quoi, en langage clair. */
export function ActivityPage() {
  const { t, i18n } = useTranslation()
  const { current } = useHousehold()
  const hid = current?.id

  const query = useQuery({
    queryKey: ['activity', hid],
    enabled: Boolean(hid),
    queryFn: () => api.get<{ items: ActivityEvent[] }>(`/households/${hid}/activity?limit=50`),
  })

  if (!current) return <p role="status">{t('common.loading')}</p>

  const items = query.data?.items ?? []

  return (
    <>
      <h1>
        <span className="row" style={{ gap: '0.5rem' }}>
          <History aria-hidden size={28} />
          {t('activity.title')}
        </span>
      </h1>
      <p className="muted">{t('activity.intro')}</p>
      <ErrorAlert error={query.error} />
      {query.isPending && <p role="status">{t('common.loading')}</p>}
      {!query.isPending && items.length === 0 && <p className="muted">{t('activity.empty')}</p>}
      {items.length > 0 && (
        <ul className="list">
          {items.map((event) => {
            const href = activityLink(event)
            const text = formatActivityMessage(t, event)
            const relative = formatRelativeTime(i18n.language, event.createdAt)
            // Origine de l'action quand ce n'est pas le site web (appli, assistant, import).
            const when = event.source === 'web' ? relative : `${relative} · ${t(`activity.source.${event.source}`)}`
            return (
              <li key={event.id}>
                {href ? (
                  <Link to={href} className="stack" style={{ textDecoration: 'none', color: 'inherit', flex: 1 }}>
                    <strong>{text}</strong>
                    <span className="muted">{when}</span>
                  </Link>
                ) : (
                  <div className="stack" style={{ flex: 1 }}>
                    <strong>{text}</strong>
                    <span className="muted">{when}</span>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
